import { useState } from 'react';
import { BiSend } from 'react-icons/bi';

import { VoiceInputButton } from '@/components';
import { appendTranscript } from '@/utils/transcribeAudio.ts';

import s from './EquipmentAIChat.module.scss';

const MAX_LENGTH = 4000;
const HINT_THRESHOLD = 3800;

interface Props {
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
  disabled: boolean;
  streaming: boolean;
  userId?: string;
  vesselId?: string;
}

const EquipmentAIChatComposer = ({
  value,
  onChange,
  onSend,
  disabled,
  streaming,
  userId,
  vesselId,
}: Props) => {
  const [transcribing, setTranscribing] = useState(false);
  const isDisabled = disabled || streaming;
  const tooLong = value.length > MAX_LENGTH;
  const showHint = value.length >= HINT_THRESHOLD;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isDisabled && value.trim() && !tooLong) {
        onSend();
      }
    }
  };

  return (
    <div className={s.composer}>
      {showHint && (
        <div className={s.charHint}>
          {value.length.toLocaleString()}&thinsp;/&thinsp;{MAX_LENGTH.toLocaleString()}
          {tooLong && ' Please shorten the message.'}
        </div>
      )}
      <div className={s.composerRow}>
        <textarea
          className={s.composerInput}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'Chat unavailable' : 'Is anything overdue for service?'}
          disabled={isDisabled}
          rows={2}
        />
        <VoiceInputButton
          source="equipment_chat"
          size={62}
          userId={userId}
          vesselId={vesselId}
          disabled={isDisabled}
          onTranscribingChange={setTranscribing}
          onTranscript={(text) => onChange(appendTranscript(value, text))}
        />
        <button
          className={s.sendButton}
          onClick={onSend}
          disabled={isDisabled || transcribing || !value.trim() || tooLong}
          type="button"
          aria-label="Send"
        >
          <BiSend />
        </button>
      </div>
    </div>
  );
};

export default EquipmentAIChatComposer;
